import { Injectable } from "@angular/core";
import { Currency } from "@front/m19-api-client";
import { formatCurrency, formatNumber, formatPercent } from "@angular/common";
import { CurrencySymbols, OrderStats } from "@front/m19-models";

export type ProfitLineItem = {
  title: string;
  value: number;
  formattedValue: string;
  share?: number;
  formattedShare?: string;
};

export type ProfitLine = ProfitLineItem & {
  type: "currency" | "absolute" | "percentage";
};

export type ProfitCategory = ProfitLineItem & {
  type: "category";
  lines: ProfitLine[];
};

export type profitLineExtractor<T> = (data: T, context: { locale: string; currency: Currency }) => ProfitLine;

@Injectable({
  providedIn: "root",
})
export class ProfitDetailsService {
  private static currencyProfitLineExtractor<T>(
    field: keyof T,
    title: string,
    totalField?: keyof T,
  ): profitLineExtractor<T> {
    return (data: T, { locale, currency }) => {
      const value = typeof data[field] === "number" ? data[field] : 0;
      const formattedValue = formatCurrency(value, locale, CurrencySymbols[currency], currency, "1.2-2");
      let share: number | undefined = undefined;
      let formattedShare: string | undefined = undefined;
      if (totalField && typeof data[totalField] === "number" && data[totalField] !== 0) {
        share = value / data[totalField];
        formattedShare = formatPercent(share, locale);
      }
      return {
        type: "currency",
        title,
        value,
        formattedValue,
        share,
        formattedShare,
      };
    };
  }

  private static absoluteProfitLineExtractor<T>(field: keyof T, title: string): profitLineExtractor<T> {
    return (data: T, { locale }) => {
      const value = typeof data[field] === "number" ? data[field] : 0;
      const formattedValue = formatNumber(value, locale, "1.0-0");
      return {
        type: "absolute",
        title,
        value,
        formattedValue,
      };
    };
  }

  private static percentageProfitLineExtractor<T>(
    title: string,
    numerator: keyof T,
    denominator: keyof T,
    coeff = 1,
  ): profitLineExtractor<T> {
    return (data: T, { locale }) => {
      let value = NaN;
      if (typeof data[denominator] === "number" && data[denominator] !== 0 && typeof data[numerator] === "number") {
        value = (coeff * data[numerator]) / data[denominator];
      }
      const formattedValue = isNaN(value) ? "N/A" : formatPercent(value, locale);
      return {
        type: "percentage",
        title,
        value,
        formattedValue,
      };
    };
  }

  private static profitCategoryExtractor<T>(
    category: string,
    profitLineExtractors: profitLineExtractor<T>[],
  ): (data: T, context: { locale: string; currency: Currency }) => ProfitCategory {
    return (data: T, context: { locale: string; currency: Currency }) => {
      const lines: ProfitLine[] = [];
      let aggregatedValue = 0;
      for (const extractor of profitLineExtractors) {
        const line = extractor(data, context);
        lines.push(line);
        if (line.type === "currency") {
          aggregatedValue += line.value;
        }
      }
      const formattedAggregatedValue = formatCurrency(
        aggregatedValue,
        context.locale,
        CurrencySymbols[context.currency],
        context.currency,
        "1.2-2",
      );
      return {
        type: "category",
        title: category,
        value: aggregatedValue,
        formattedValue: formattedAggregatedValue,
        lines,
      };
    };
  }

  private static readonly NetSalesCategory = ProfitDetailsService.profitCategoryExtractor<OrderStats>(
    "metrics.ORDER_GLOBAL_NET_SALES_title",
    [
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "grossSales",
        "metrics.ORDER_SALES_title",
        "grossSales",
      ),
      ProfitDetailsService.absoluteProfitLineExtractor<OrderStats>("quantity", "metrics.QUANTITY_title"),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "promotion",
        "metrics.PROMOTION_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>("points", "metrics.POINTS_title", "grossSales"),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "refundedSales",
        "metrics.REFUNDED_SALES_title",
        "grossSales",
      ),
      ProfitDetailsService.absoluteProfitLineExtractor<OrderStats>(
        "sellableReturnUnits",
        "metrics.SELLABLE_RETURNS_title",
      ),
    ],
  );

  private static readonly AdvertisingCategory = ProfitDetailsService.profitCategoryExtractor(
    "metrics.ORDER_GLOBAL_ADV_title",
    [
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>("advertising", "common.sponsored_ads"),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>("dspAdvertising", "common.dsp"),
    ],
  );

  private static readonly FeesCategory = ProfitDetailsService.profitCategoryExtractor<OrderStats>(
    "metrics.ORDER_GLOBAL_FEE_title",
    [
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "referralFee",
        "metrics.REFERRAL_FEE_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>("fbaFee", "metrics.FBA_FEE_title", "grossSales"),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "fbaStorageFee",
        "metrics.FBA_STORAGE_FEE_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "longTermStorageFee",
        "metrics.FBA_LONG_TERM_STORAGE_FEE_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "liquidations",
        "metrics.LIQUIDATIONS_FEE_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "internationalFreight",
        "metrics.INTERNATIONAL_FREIGHT_FEE_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>("otherFee", "metrics.OTHER_FEE_title", "grossSales"),
    ],
  );

  private static readonly ReimbursementsCategory = ProfitDetailsService.profitCategoryExtractor<OrderStats>(
    "metrics.REIMBURSEMENT_title",
    [
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "fbaReversalReimbursement",
        "metrics.FBA_REVERSAL_REIMBURSEMENT_title",
        "grossSales",
      ),
      ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
        "fbaOtherReimbursement",
        "metrics.FBA_OTHER_REIMBURSEMENT_title",
        "grossSales",
      ),
    ],
  );

  private static readonly CostOfGoods = ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
    "costOfGoods",
    "metrics.COST_OF_GOODS_title",
    "grossSales",
  );

  private static readonly Profit = ProfitDetailsService.currencyProfitLineExtractor<OrderStats>(
    "profit",
    "metrics.PROFIT_DETAIL_AMOUNT_title",
    "grossSales",
  );
  private static readonly ROI = ProfitDetailsService.percentageProfitLineExtractor<OrderStats>(
    "metrics.ROI_title",
    "profit",
    "costOfGoods",
    -1,
  );
  private static readonly Margin = ProfitDetailsService.percentageProfitLineExtractor<OrderStats>(
    "metrics.ORDER_GLOBAL_MARGIN_title",
    "profit",
    "netSales",
  );

  private static readonly ProfitLayout = [
    ProfitDetailsService.NetSalesCategory,
    ProfitDetailsService.AdvertisingCategory,
    ProfitDetailsService.FeesCategory,
    ProfitDetailsService.ReimbursementsCategory,
    ProfitDetailsService.CostOfGoods,
    ProfitDetailsService.Profit,
    ProfitDetailsService.ROI,
    ProfitDetailsService.Margin,
  ];

  public getAllProfitLines(data: OrderStats, locale: string, currency: Currency): (ProfitLine | ProfitCategory)[] {
    return ProfitDetailsService.ProfitLayout.map((extractor) => extractor(data, { locale, currency }));
  }
}
